import {Injectable} from '@angular/core';
import {catchError, forkJoin, map, Observable, of, shareReplay, tap} from 'rxjs';

import {RestService} from 'src/app/shared/services/rest.service';
import {StatisticsResponse} from '../models/statistics.model';

/**
 * Laedt die Jahresstatistik und haelt die bereits abgerufenen Jahre vor.
 *
 * Der Abruf dauert je nach Datenmenge bis zu einer Sekunde, der Verlauf und
 * der Vorjahresvergleich brauchen aber weitere Jahre. Deshalb der Cache: das
 * gewaehlte Jahr wird zuerst geladen und angezeigt, die uebrigen Jahre kommen
 * im Hintergrund nach und machen Jahreswechsel danach verzoegerungsfrei.
 *
 * Wird auf Seitenebene bereitgestellt, damit ein erneuter Aufruf der Seite
 * frische Zahlen zieht.
 */
@Injectable()
export class StatisticsService {
  private readonly cache = new Map<string, Observable<StatisticsResponse>>();

  constructor(private readonly _restService: RestService) {
  }

  /** Statistik eines Jahres, aus dem Cache sofern bereits geladen. */
  load(year: number, shelterId: number | null): Observable<StatisticsResponse> {
    const key = this.cacheKey(year, shelterId);
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    let url = `/statistics?year=${year}`;
    if (shelterId !== null) {
      url += `&animalShelter=${shelterId}`;
    }

    const request$ = this._restService.httpGet(url).pipe(
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.cache.set(key, request$);

    // Ein fehlgeschlagener Abruf darf nicht dauerhaft im Cache liegen bleiben.
    return request$.pipe(
      tap({error: () => this.cache.delete(key)})
    );
  }

  /**
   * Laedt die uebrigen Jahre fuer Verlauf und Vorjahresvergleich nach.
   * Fehler einzelner Jahre werden geschluckt - der Verlauf ist Beiwerk.
   */
  loadYears(years: number[], shelterId: number | null): Observable<Map<number, StatisticsResponse>> {
    if (!years.length) {
      return of(new Map<number, StatisticsResponse>());
    }

    const requests = years.map(year => this.load(year, shelterId).pipe(
      map(response => [year, response] as [number, StatisticsResponse | null]),
      // Ein kaputtes Jahr soll den Verlauf nicht komplett verhindern.
      catchError(() => of([year, null] as [number, StatisticsResponse | null]))
    ));

    return forkJoin(requests).pipe(
      map(entries => new Map(
        entries.filter((entry): entry is [number, StatisticsResponse] => !!entry[1])
      ))
    );
  }

  /** Bereits geladenes Jahr, ohne neuen Abruf. */
  peek(year: number, shelterId: number | null): Observable<StatisticsResponse> | undefined {
    return this.cache.get(this.cacheKey(year, shelterId));
  }

  clear(): void {
    this.cache.clear();
  }

  private cacheKey(year: number, shelterId: number | null): string {
    return `${shelterId ?? 'all'}:${year}`;
  }
}
